import { css, cx } from "@emotion/css";
import { Avatar, useIsHovered, useIsLargerThanIpadResolution, useIsMobile } from "@octopusdeploy/design-system-components";
import { space, text, themeTokens, borderRadius } from "@octopusdeploy/design-system-tokens";
import type { DetailedDashboardProject, InitialDashboardProject } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { useRef } from "react";
import * as React from "react";
import { ProjectFavouriteIconButton, useOptimisticIsFavourite } from "~/areas/projects/components/ProjectLayout/ProjectFavouriteIconButton";
import { projectStatusActions } from "~/areas/projects/components/ProjectStatus/actions";
import type { ProjectStatus } from "~/areas/projects/components/ProjectStatus/useProjectStatus";
import type { FavouriteProjectsData } from "~/areas/projects/components/Projects/ProjectsDashboard";
import { repository } from "~/clientInstance";
import FrozenIcon, { FrozenResourceType } from "~/components/DeploymentFreezes/FrozenIcon";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
import { Skeleton } from "~/components/Skeleton/Skeleton";
interface ProjectsDashboardRowHeaderProps {
    favouriteProjectsData: FavouriteProjectsData;
    project: DetailedDashboardProject | InitialDashboardProject;
    projectStatus: ProjectStatus;
}
export function ProjectsDashboardRowHeaderContent({ favouriteProjectsData, project, projectStatus }: ProjectsDashboardRowHeaderProps) {
    const isLargerThanIpad = useIsLargerThanIpadResolution();
    const isFavourite = favouriteProjectsData.favouriteProjects.find((p) => p.Id === project.Id) !== undefined;
    const setIsFavourite = (isFavourite: boolean) => favouriteProjectsData.onFavouriteProjectChanged(project.Id, isFavourite);
    return (<div className={cx(projectRowHeaderStyles, { [projectRowHeaderMobileStyles]: !isLargerThanIpad })}>
            <ProjectLink key={project.Id} project={project} isFavourite={isFavourite} setIsFavourite={setIsFavourite} projectStatus={projectStatus}/>
        </div>);
}
export function ProjectsDashboardRowHeaderSkeleton() {
    const isLargerThanIpad = useIsLargerThanIpadResolution();
    return (<td className={cx(tableCellStyles, projectRowHeaderStyles, { [projectRowHeaderMobileStyles]: !isLargerThanIpad })}>
            <ProjectLinkSkeleton />
        </td>);
}
const tableCellStyles = css({
    padding: 0,
});
const projectRowHeaderStyles = css({
    display: "flex",
    flexDirection: "column",
    width: "18.625rem",
    minWidth: "18.625rem",
    textAlign: "start",
});
const projectRowHeaderMobileStyles = css({
    width: "12rem",
    minWidth: "12rem",
});
interface ProjectLinkProps {
    isFavourite: boolean;
    setIsFavourite: (isFavourite: boolean) => Promise<boolean>;
    project: DetailedDashboardProject | InitialDashboardProject;
    projectStatus: ProjectStatus;
}
function ProjectLink({ project, setIsFavourite, isFavourite, projectStatus }: ProjectLinkProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const isHovered = useIsHovered(containerRef);
    const getIsFavouriteButtonVisible = (isFavourite: boolean) => isFavourite || isHovered;
    const { Name, ProjectLogo, Id, IsDisabled, IsFrozen } = project;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const projectUrl = links.projectRootRedirect.generateUrl({ spaceId: repository.spaceId!, projectSlug: Id });
    return (<div ref={containerRef} className={projectLinkRootStyles}>
            <ProjectLinkFavouriteButton isFavourite={isFavourite} setIsFavourite={setIsFavourite} getIsVisible={getIsFavouriteButtonVisible} projectName={Name}/>
            <InternalLink className={projectLinkStyles} to={projectUrl} onClick={() => {
            projectStatus.dispatch({ type: projectStatusActions.guidedSetupResumed });
        }}>
                <div className={projectAvatarWrapperStyles}>
                    <Avatar type="project" size="medium" src={ProjectLogo} alt={`${Name} Logo`} isMuted={IsDisabled}/>
                </div>
                <p className={cx(projectNameStyles, { [projectNameDisabledStyles]: IsDisabled })}>{Name}</p>
                {IsFrozen && <FrozenIcon for={FrozenResourceType.Project}/>}
            </InternalLink>
        </div>);
}
function ProjectLinkSkeleton() {
    const isMobile = useIsMobile();
    return (<div className={projectLinkRootStyles}>
            <div className={projectLinkFavouriteButtonStyle.favouriteButtonContainer.root}>
                <div className={favouriteButtonPlaceholderStyles}/>
            </div>
            <div className={projectLinkStyles}>
                <div className={avatarSkeletonStyles}>
                    <Skeleton shape={"Rounded"} borderRadius={"small"}/>
                </div>
                <span className={cx(projectNameSkeletonStyles, { [projectNameSkeletonMobileStyles]: isMobile })}>
                    <Skeleton shape={"Rounded"} borderRadius={"small"}/>
                </span>
            </div>
        </div>);
}
const favouriteButtonPlaceholderStyles = css({
    height: "1.25rem",
    width: "1.25rem",
});
const avatarSkeletonStyles = css({
    height: "2.25rem",
    width: "2.25rem",
});
const projectNameSkeletonStyles = css({
    height: "1.25rem",
    width: "10.75rem",
});
const projectNameSkeletonMobileStyles = css({
    width: "5rem",
});
const projectLinkRootStyles = css({
    display: "flex",
    alignItems: "center",
    padding: `${space[12]} 0`,
});
const projectLinkStyles = css({
    display: "flex",
    gap: space[12],
    alignItems: "center",
    font: text.body.bold.medium,
    lineHeight: "1.25rem",
    color: `${themeTokens.color.text.primary} !important`,
    flex: 1,
    padding: `${space[6]} ${space[16]} ${space[6]} 0`,
    minHeight: "3.25rem",
    "&:hover": {
        span: {
            color: themeTokens.color.text.link.default,
        },
    },
    ":focus-visible": {
        boxShadow: themeTokens.shadow.focused,
        outline: "none",
        borderRadius: borderRadius.small,
    },
});
const projectAvatarWrapperStyles = css({
    position: "relative",
    alignSelf: "center",
});
const projectNameStyles = css({
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "break-word",
    display: "-webkit-box",
    WebkitLineClamp: "2",
    WebkitBoxOrient: "vertical",
    margin: 0,
});
const projectNameDisabledStyles = css({
    color: themeTokens.color.text.disabled,
});
interface ProjectLinkFavouriteButtonProps {
    isFavourite: boolean;
    setIsFavourite: (isFavourite: boolean) => Promise<boolean>;
    getIsVisible: (isFavourite: boolean) => boolean;
    projectName: string;
}
function ProjectLinkFavouriteButton({ isFavourite, setIsFavourite, projectName, getIsVisible }: ProjectLinkFavouriteButtonProps) {
    const { optimisticIsFavourite, optimisticSetIsFavourite, isPendingUpdate } = useOptimisticIsFavourite(isFavourite, setIsFavourite);
    return (<div className={cx(projectLinkFavouriteButtonStyle.favouriteButtonContainer.root, getIsVisible(optimisticIsFavourite) ? projectLinkFavouriteButtonStyle.favouriteButtonContainer.visible : projectLinkFavouriteButtonStyle.favouriteButtonContainer.hidden)}>
            <ProjectFavouriteIconButton isFavourite={optimisticIsFavourite} setIsFavourite={optimisticSetIsFavourite} isDisabled={isPendingUpdate} projectName={projectName}/>
        </div>);
}
const projectLinkFavouriteButtonStyle = {
    favouriteButtonContainer: {
        root: css({
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: `${space[8]} ${space[8]} ${space[8]} ${space[16]}`,
        }),
        hidden: css({
            visibility: "hidden",
        }),
        visible: css({
            visibility: "visible",
        }),
    },
};
